import dayjs from 'dayjs';
import {
  ACTIVE_STATUSES,
  UPCOMING_STATUSES,
  HISTORY_STATUSES,
  DELETED_STATUSES,
  POST_PICKUP_STATUSES,
  STATUS_COLOR_MAP,
  STATUS_LABEL_MAP,
  GUESTRIDE_STATUS,
  type GuestrideStatus,
  type GuestRideTab,
} from '../constants/guestRideConstants';

export interface PickedLocation {
  address: string;
  lat: number;
  lng: number;
}

// ---------- Status classification ---------- //

export const isActiveStatus = (status: string): boolean =>
  ACTIVE_STATUSES.includes(status as GuestrideStatus);

export const isUpcomingStatus = (status: string): boolean =>
  UPCOMING_STATUSES.includes(status as GuestrideStatus);

export const isHistoryStatus = (status: string): boolean =>
  HISTORY_STATUSES.includes(status as GuestrideStatus);

export const isDeletedStatus = (status: string): boolean =>
  DELETED_STATUSES.includes(status as GuestrideStatus);

export const isAtOrPastPickup = (status: string): boolean =>
  POST_PICKUP_STATUSES.includes(status as GuestrideStatus);

export const getTabForStatus = (status: string): GuestRideTab => {
  if (isDeletedStatus(status)) return 'deleted';
  if (isActiveStatus(status)) return 'active';
  if (isUpcomingStatus(status)) return 'upcoming';
  return 'history';
};

// ---------- Optimistic status overlay ---------- //
//
// When a dealer calls/retries a ride we optimistically render it as `pending`
// so the Call/Retry button stops lingering. These pure helpers carry the
// merge + expiry rules so they can be unit-tested independently of the React
// hook (`useGuestRides`) that drives them. See `OptimisticRideStatus` in
// `../stores/guestRideStore` for the overlay entry shape.

interface OptimisticEntry {
  status: string;
  baseline: string;
  at: number;
}

// Overlay the optimistic status onto each ride. A ride keeps its optimistic
// status only while the server still reports the baseline it had at call time;
// once the server moves it (to pending, booked, failed, …) we leave the real
// status in place so the row reflects the truth.
export const applyOptimisticStatuses = <T extends { id: number; status: string }>(
  rides: T[],
  optimisticStatuses: Record<number, OptimisticEntry>,
): T[] => {
  if (!rides?.length) return rides ?? [];
  if (!optimisticStatuses || Object.keys(optimisticStatuses).length === 0) return rides;
  return rides.map(ride => {
    const optimistic = optimisticStatuses[ride.id];
    if (!optimistic || ride.status !== optimistic.baseline) return ride;
    return { ...ride, status: optimistic.status };
  });
};

// Decide whether a single optimistic overlay entry is now stale and should be
// dropped: the ride fell out of the current list, the server moved off the
// baseline (real status now drives the row), or the TTL elapsed without any
// server change (a silently-failed call — bring the button back).
export const isOptimisticStatusStale = (
  optimistic: OptimisticEntry,
  ride: { status: string } | undefined,
  now: number,
  ttlMs: number,
): boolean => {
  if (!ride) return true;
  if (ride.status !== optimistic.baseline) return true;
  return now - optimistic.at >= ttlMs;
};

// ---------- Formatting ---------- //

export const formatGuestRideStatus = (status: string): string => {
  if (!status) return 'Unknown';
  const known = STATUS_LABEL_MAP[status as GuestrideStatus];
  if (known) return known;
  return status
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getStatusChipColor = (status: string) => {
  return STATUS_COLOR_MAP[status as GuestrideStatus] || 'default';
};

export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatDateTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return dayjs(dateStr).format('M/D h:mm A');
};

export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return dayjs(dateStr).format('M/D/YYYY');
};

export const formatTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return dayjs(dateStr).format('h:mm A');
};

export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// ---------- Search ---------- //

export const matchesSearch = (ride: any, searchText: string): boolean => {
  if (!searchText) return true;
  const term = searchText.toLowerCase();
  const ridehail = ride?.ridehails?.[0];

  const fields = [
    ride?.guest_name,
    ride?.guest_phone,
    ride?.pickup_address,
    ride?.dropoff_address,
    ride?.provider,
    ride?.status,
    ride?.notes,
    ride?.id?.toString(),
    ridehail?.driver_name,
    ridehail?.pickup_address,
    ridehail?.dropoff_address,
    ridehail?.provider,
  ];

  return fields.some(f => f?.toLowerCase?.().includes(term));
};

// ---------- Cost helpers ---------- //

export const getRideCost = (ride: any): number | null => {
  const ridehail = ride?.ridehails?.[0];
  if (!ridehail) return null;
  return ridehail.actual_price ?? ridehail.estimated_price ?? null;
};

export const getTotalSpend = (rides: any[]): number => {
  return rides.reduce((sum, ride) => {
    const cost = getRideCost(ride);
    return sum + (cost ?? 0);
  }, 0);
};

// ---------- Duration helpers ---------- //

/** Elapsed minutes between the ridehail's pickup_at and dropoff_at, or null if either is missing/invalid. */
export const getRideDurationMinutes = (ride: any): number | null => {
  const ridehail = ride?.ridehails?.[0];
  if (!ridehail?.pickup_at || !ridehail?.dropoff_at) return null;
  const minutes = dayjs(ridehail.dropoff_at).diff(dayjs(ridehail.pickup_at), 'minute');
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
};

export const formatDuration = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins === 0 ? `${hours} hr` : `${hours} hr ${mins} min`;
};

// ---------- Ridehail helpers ---------- //

export const getLatestRidehail = (ride: any) => {
  return ride?.ridehails?.[0] ?? null;
};

export const getDriverInfo = (ride: any) => {
  const ridehail = getLatestRidehail(ride);
  if (!ridehail) return null;
  return {
    name: ridehail.driver_name,
    phone: ridehail.driver_phone,
    vehicle: [ridehail.vehicle_color, ridehail.vehicle_year, ridehail.vehicle_make, ridehail.vehicle_model]
      .filter(Boolean)
      .join(' '),
    licensePlate: ridehail.vehicle_license_plate,
    photoUrl: ridehail.driver_photo_url,
    vehiclePhotoUrl: ridehail.vehicle_photo_url,
  };
};

export const isFailed = (ride: any): boolean => {
  return ride?.status === GUESTRIDE_STATUS.FAILED;
};
